import { Currency, PriceDropPace, UpdateProductInput } from "@/Apollo/schema";
import { toPriceAmount } from "@/common/utils/seller-price.utils";
import {
  CREATE_AD_V2_DEFAULT_FLOOR_SHARE,
  CREATE_AD_V2_MAX_PRICE,
  CREATE_AD_V2_MIN_BID_SHARE,
  CREATE_AD_V2_MIN_PRICE,
  CREATE_AD_V2_SMART_DROP_MAX_STEP,
  CREATE_AD_V2_SMART_DROP_MIN_STEP,
} from "@/features/create-ad-v2/static/create-ad-v2.static";

/** Why typed text is not an amount: not a number, has cents, or is beyond the money columns. */
export type CreateAdV2EurosError = "invalid" | "decimal" | "too-large";

/** A parsed amount. Empty text is no value and no error; the caller decides if it is required. */
export type CreateAdV2Euros =
  | { value: number; error: null }
  | { value: null; error: CreateAdV2EurosError | null };

const WHOLE_EUROS = /^(\d+|\d{1,3}([.,]\d{3})+)$/;
const WITH_CENTS = /^(\d+|\d{1,3}([.,]\d{3})+)[.,]\d{1,2}$/;

/**
 * Whole euros from what the seller typed, for the Smart Drop floor only: the gateway stores the
 * floor in whole euros, rounding up (`toWholeFloor`), so a floor with cents would save a
 * different amount. Spaces, a euro sign and thousands separators are read ("1.250", "1,250" and
 * "1 250" are all 1250); a decimal part is refused rather than swallowed, so "4,95" never
 * becomes 495; anything above {@link CREATE_AD_V2_MAX_PRICE} is refused before it reaches the
 * database. The floor moves to {@link parseCreateAdV2Amount}, cents included, once the gateway
 * stores them (WHO-6569); every other seller amount uses it already (WHO-6543).
 */
export const parseCreateAdV2Euros = (text: string): CreateAdV2Euros => {
  const compact = text.replace(/[\s€]/g, "");

  if (!compact) {
    return { value: null, error: null };
  }

  if (WITH_CENTS.test(compact)) {
    return { value: null, error: "decimal" };
  }

  if (!WHOLE_EUROS.test(compact)) {
    return { value: null, error: "invalid" };
  }

  const value = Number(compact.replace(/[.,]/g, ""));

  if (value > CREATE_AD_V2_MAX_PRICE) {
    return { value: null, error: "too-large" };
  }

  return { value, error: null };
};

/**
 * A seller's own amount on create ad v2: the asking price, the price when new, a custom shipping
 * price. It is read as every seller price form reads it (`toPriceAmount`, WHO-6302): cents with
 * either separator (`302,50`, `302.50`, `€ 302,50`), and anything else refused as "invalid", never
 * guessed. A thousands separator (`1.250`, `1,250`, `25.000`) or a third decimal is refused: `1.250`
 * is a thousand in Dutch and one and a quarter in English (WHO-6543). Anything above
 * {@link CREATE_AD_V2_MAX_PRICE} is refused before it reaches the database.
 */
export const parseCreateAdV2Amount = (text: string): CreateAdV2Euros => {
  if (!text.trim()) {
    return { value: null, error: null };
  }

  const value = toPriceAmount(text);

  if (value === null) {
    return { value: null, error: "invalid" };
  }

  if (value > CREATE_AD_V2_MAX_PRICE) {
    return { value: null, error: "too-large" };
  }

  return { value, error: null };
};

/** Characters a money field keeps while typing: digits, separators, spaces and the euro sign. */
export const cleanCreateAdV2MoneyText = (text: string) =>
  text.replace(/[^\d.,\s€]/g, "");

/** The advice's scale runs from €0 to this many times the top of the range (Dieke's mockup). */
const PRICE_SCALE_HIGH_FACTOR = 1.5;

/** Where a price sits on the advice's scale, 0..100: €0 to 1.5 × the top of the range, pinned at the ends. */
export const getCreateAdV2PriceScalePercent = (value: number, high: number) => {
  const max = high * PRICE_SCALE_HIGH_FACTOR;

  if (max <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (value / max) * 100));
};

/** Within this share of the advice, a price is "close to our advice". */
const ADVICE_BAND = 0.1;

/**
 * What a price means for selling, against the comparable sales (Dieke's five bands, plus the
 * advice itself, as prefilled): below the lowest sales, the lower part of the range, close to the
 * advice (within 10%), the upper part of the range, or above the highest sales.
 */
export type CreateAdV2PriceBand =
  | "advice"
  | "below"
  | "lower"
  | "close"
  | "upper"
  | "above";

export const getCreateAdV2PriceBand = (
  price: number,
  { low, advice, high }: { low: number; advice: number; high: number }
): CreateAdV2PriceBand => {
  if (price === Math.round(advice)) {
    return "advice";
  }

  if (price < low) {
    return "below";
  }

  if (price < advice * (1 - ADVICE_BAND)) {
    return "lower";
  }

  if (price <= advice * (1 + ADVICE_BAND)) {
    return "close";
  }

  if (price <= high) {
    return "upper";
  }

  return "above";
};

/** Prices this far above the high anchor take longer to sell; this far below the low one look like a typo. */
const UNUSUAL_HIGH_FACTOR = 2;
const UNUSUAL_LOW_FACTOR = 3;

/**
 * A price far outside the comparable sales: more than twice the high anchor ("Expect it to take
 * longer", a note), or below a third of the low one (a typo, often: the seller confirms it).
 * Null when it is plausible.
 */
export const getCreateAdV2UnusualPrice = (
  price: number,
  low: number,
  high: number
): "high" | "low" | null => {
  if (price > high * UNUSUAL_HIGH_FACTOR) {
    return "high";
  }

  if (price < low / UNUSUAL_LOW_FACTOR) {
    return "low";
  }

  return null;
};

/** How far below the lowest comparable sale a price is, as a whole percentage: 87 for €90 against €714. */
export const getCreateAdV2PercentBelow = (price: number, low: number) =>
  low > 0 ? Math.round(((low - price) / low) * 100) : 0;

/**
 * The floor prefilled from the asking price: 70%, rounded down to whole euros. Smart Drop never
 * goes below it, and bids below it never reach the seller.
 */
export const getCreateAdV2DefaultFloor = (price: number) =>
  /* In cents first: 90 × 0.7 is 62.99999… in floating point, and the floor is €63. */
  Math.floor(Math.round(price * CREATE_AD_V2_DEFAULT_FLOOR_SHARE * 100) / 100);

/** Smart Drop drops the price step by step to the floor; a fixed price stays. */
export type CreateAdV2PriceMode = "smart" | "fixed";

/** What the price step saves. */
export type CreateAdV2PriceValues = {
  price: number;
  allowBid: boolean;
  mode: CreateAdV2PriceMode;
  pace: PriceDropPace;
  stepPercent: number;
  /**
   * Smart Drop: "Never lower than", also the lowest bid that reaches the seller. Fixed price
   * with bids: the lowest bid that reaches the seller. Null with a fixed price and no bids.
   */
  floor: number | null;
};

/**
 * The `updateProduct` input for the price step. Smart Drop sends its settings (the gateway also
 * makes the floor the minimum bid while bids are on, and the floor is sent as the minimum bid
 * too so both always agree); a fixed price switches the drops off and keeps the seller's lowest
 * bid as the minimum bid.
 */
export const toCreateAdV2PriceInput = (
  values: CreateAdV2PriceValues
): UpdateProductInput => {
  const minimumBid =
    values.allowBid && values.floor !== null
      ? { amount: values.floor, currency: Currency.Eur }
      : null;

  return {
    buy_now_price: { amount: values.price, currency: Currency.Eur },
    allow_buy_now: true,
    allow_bid: values.allowBid,
    minimum_bid: minimumBid,
    priceDrop:
      values.mode === "smart" && values.floor !== null
        ? {
            enabled: true,
            pace: values.pace,
            stepPercent: values.stepPercent,
            floor: values.floor,
          }
        : { enabled: false },
  };
};

export type CreateAdV2PriceErrors = {
  price?: "required" | "minimum" | CreateAdV2EurosError;
  floor?:
    | "required"
    | "not-below-price"
    | "above-price"
    | "minimum"
    | CreateAdV2EurosError;
  step?: "range";
};

type CreateAdV2PriceInputs = {
  price: CreateAdV2Euros;
  mode: CreateAdV2PriceMode;
  allowBid: boolean;
  /** "Never lower than" with Smart Drop, "Bids below" with a fixed price and bids. */
  floor: CreateAdV2Euros;
  /** Smart Drop's step in percent; null when the typed one is not a whole number. */
  stepPercent: number | null;
};

/**
 * The asking price is an amount (cents allowed) of at least {@link CREATE_AD_V2_MIN_PRICE}. Smart Drop
 * needs a floor above 0 and below the asking price, and a step of 1 to 25%. With bids on, the
 * lowest bid that reaches the seller (the floor) is at least
 * {@link CREATE_AD_V2_MIN_BID_SHARE} of the asking price, as the publish gate wants; with a
 * fixed price it may not be above the asking price. Text that did not parse reports why, so the
 * seller sees what to fix rather than a changed number.
 */
export const validateCreateAdV2Price = ({
  price,
  mode,
  allowBid,
  floor,
  stepPercent,
}: CreateAdV2PriceInputs): CreateAdV2PriceErrors => {
  const errors: CreateAdV2PriceErrors = {};
  const priceValue = price.value;

  if (price.error) {
    errors.price = price.error;
  } else if (!priceValue) {
    errors.price = "required";
  } else if (priceValue < CREATE_AD_V2_MIN_PRICE) {
    errors.price = "minimum";
  }

  if (
    mode === "smart" &&
    (stepPercent === null ||
      stepPercent < CREATE_AD_V2_SMART_DROP_MIN_STEP ||
      stepPercent > CREATE_AD_V2_SMART_DROP_MAX_STEP)
  ) {
    errors.step = "range";
  }

  if (mode === "fixed" && !allowBid) {
    return errors;
  }

  const floorValue = floor.value;
  const minimumBid = priceValue
    ? Math.ceil(priceValue * CREATE_AD_V2_MIN_BID_SHARE)
    : null;

  if (floor.error) {
    errors.floor = floor.error;
  } else if (!floorValue) {
    errors.floor = "required";
  } else if (mode === "smart" && priceValue && floorValue >= priceValue) {
    errors.floor = "not-below-price";
  } else if (mode === "fixed" && priceValue && floorValue > priceValue) {
    errors.floor = "above-price";
  } else if (allowBid && minimumBid !== null && floorValue < minimumBid) {
    errors.floor = "minimum";
  }

  return errors;
};

type CreateAdV2SavedPrice = {
  price?: number | null;
  allowBid?: boolean | null;
  minimumBid?: number | null;
  /** The product's `priceDropSettings`: stored values with the gateway's defaults filled in. */
  settings?: {
    enabled: boolean;
    pace: PriceDropPace;
    stepPercent: number;
    floor?: { amount: number } | null;
  } | null;
};

/**
 * What the price step saved on the draft, read back: Smart Drop with its pace, step and floor,
 * or a fixed price with the lowest bid that reaches the seller (the minimum bid). Null before a
 * price was saved, or without the settings, so the step starts from the advice.
 */
export const readCreateAdV2SavedPrice = ({
  price,
  allowBid,
  minimumBid,
  settings,
}: CreateAdV2SavedPrice): CreateAdV2PriceValues | null => {
  if (!price || !settings) {
    return null;
  }

  const bids = !!allowBid;

  return {
    price,
    allowBid: bids,
    mode: settings.enabled ? "smart" : "fixed",
    pace: settings.pace,
    stepPercent: settings.stepPercent,
    floor: settings.enabled
      ? settings.floor?.amount ?? null
      : bids
      ? minimumBid ?? null
      : null,
  };
};
